import { QueryCache, QueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

// Central React Query configuration (#261 Gap 4).
//
// Previously `App.tsx` did `new QueryClient()` with NO `defaultOptions`, which
// left every query on React Query's stock behaviour: retry a *failed* query 3×
// (even on a 4xx that will never succeed), no global `onError`, so a query that
// ultimately failed just stopped — spinning forever with the failure swallowed
// and nothing in Sentry or the console. This module makes failures observable
// and the retry policy sane, without surfacing intrusive UI.
//
// SCOPE (#261 — config / network path only): this touches request retry,
// caching, and error *observability*. It does NOT touch the render loop or any
// per-frame work, so it has zero effect on RTX 5070-class rendering.

/**
 * Best-effort extraction of an HTTP-ish status code from an unknown error.
 *
 * Supabase (`PostgrestError`/`FunctionsHttpError`), `fetch` `Response`s, and
 * thrown plain objects all surface status differently — and sometimes only on
 * the wrapped `cause`. We probe the common shapes (`status`, `statusCode`,
 * `response.status`) on both the error and its `cause`, returning the first
 * finite number we find, else `undefined`.
 */
function extractStatus(error: unknown): number | undefined {
  const fromOne = (e: unknown): number | undefined => {
    if (!e || typeof e !== 'object') return undefined;
    const obj = e as Record<string, unknown>;
    const candidates: unknown[] = [
      obj.status,
      obj.statusCode,
      (obj.response as Record<string, unknown> | undefined)?.status,
    ];
    for (const c of candidates) {
      if (typeof c === 'number' && Number.isFinite(c)) return c;
      // Some libraries stringify the status (e.g. PostgrestError.code can be a
      // numeric-looking string). Only accept it if it parses to a clean int.
      if (typeof c === 'string' && /^\d{3}$/.test(c)) return Number(c);
    }
    return undefined;
  };

  return (
    fromOne(error) ?? fromOne((error as { cause?: unknown } | null)?.cause)
  );
}

/**
 * True when the error looks like a client error (HTTP 4xx) — i.e. a request
 * that is malformed, unauthorized, or otherwise will NOT succeed on retry.
 */
export function isClientError(error: unknown): boolean {
  const status = extractStatus(error);
  return status !== undefined && status >= 400 && status < 500;
}

/**
 * PURE retry predicate (exported for unit testing).
 *
 * - Never retry a 4xx — it won't succeed; retrying just wastes round-trips and
 *   delays surfacing the failure.
 * - Otherwise retry up to a bound: `failureCount < 2` → at most 2 retries
 *   (3 total attempts), then give up and let `onError` make it observable.
 *
 * `failureCount` is React Query's 1-based count of attempts that have already
 * failed (1 after the first failure, etc.).
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isClientError(error)) return false;
  return failureCount < 2;
}

/**
 * Bounded exponential backoff: 1s, 2s, 4s … capped at 30s. Keeps the (few)
 * retries from hammering a struggling backend.
 */
export function retryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 30_000);
}

/**
 * Build the singleton app QueryClient with deliberate defaults.
 *
 * Per-query overrides in the data layer (e.g. `profileService` 24h `staleTime`,
 * the `conversationsQuery` `*_STALE_TIME` constants, `useMessagesQuery`'s
 * `initialData: []`) all still win over these globals — React Query merges
 * per-query options on top of `defaultOptions`. These defaults only fill the
 * gaps for queries that specify none.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    // Global cache-level error hook. Fires once per query that has *exhausted*
    // its retries and still failed — the silent-failure case. Non-intrusive:
    // we make the failure observable (Sentry + console), we do NOT raise a
    // toast (per-feature UI owns user-facing messaging).
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Guard the Sentry call like ErrorView does: it may be a no-op (not
        // initialised) and must never throw out of an error handler.
        try {
          Sentry.captureException(error, {
            tags: { source: 'react-query' },
            extra: { queryKey: query.queryKey },
          });
        } catch {
          // ignore — Sentry must not mask the original failure
        }
        console.error('[react-query] query failed', query.queryKey, error);
      },
    }),
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay,
        // A small staleTime collapses the refetch storms that fire when many
        // components mount the same query at once; anything needing fresher or
        // longer-lived data sets its own staleTime explicitly.
        staleTime: 30_000,
        // Deliberately NOT disabled. The billing-status query in AuthProvider
        // documents a hard reliance on refetch-on-focus to catch the
        // return-from-Stripe-checkout case, so we keep React Query's default
        // (refetch on focus) rather than globally turning it off.
        refetchOnWindowFocus: true,
      },
      mutations: {
        // Mutations are not idempotent in general — never auto-retry them.
        retry: 0,
      },
    },
  });
}

/**
 * Spread into a `useQuery` that uses `initialData: []` but must still fetch on
 * mount. The 30s `staleTime` above treats freshly-provided `initialData` as
 * fresh-as-of-mount, so without this the *initial* fetch is suppressed and a
 * cold load (e.g. an editor reopen or a public share link) renders the empty
 * list until some unrelated refetch trigger fires. Stamping the seed data
 * stale-since-epoch forces the on-mount fetch; the `staleTime` still dedupes
 * subsequent same-key mounts.
 */
export const FETCH_ON_MOUNT = { initialDataUpdatedAt: 0 } as const;
